// PHOENIX: Server time synchronization module
// Ensures all devices use DATABASE time, not local device time

import { supabase } from "@/integrations/supabase/client";

let serverTimeOffset = 0; // Difference between server time and local time
let lastSyncTime = 0;
const SYNC_INTERVAL = 60000; // Sync every 60 seconds

/**
 * Fetch current server time and calculate offset
 * Call this once on app load, then periodically
 */
export async function syncServerTime(): Promise<void> {
  const now = Date.now();
  
  // Only sync if 60 seconds have passed
  if (now - lastSyncTime < SYNC_INTERVAL) {
    return;
  }
  
  try {
    const { data, error } = await supabase.rpc('get_server_time_info');
    
    if (error || !data) {
      console.warn('[ServerTime] Failed to sync server time:', error);
      return;
    }
    
    const serverTimeMs = data.server_now_epoch_ms as number;
    const localTimeMs = Date.now();
    
    // Calculate offset: server_time - local_time
    serverTimeOffset = serverTimeMs - localTimeMs;
    lastSyncTime = now;
    
    console.log(`[ServerTime] Synced! Offset: ${serverTimeOffset}ms (${Math.round(serverTimeOffset / 1000 / 60)}min)`);
  } catch (err) {
    console.error('[ServerTime] Sync error:', err);
  }
}

/**
 * Get current time using server offset
 * This ensures ALL devices use the same reference time
 */
export function getServerNow(): number {
  return Date.now() + serverTimeOffset;
}

/**
 * Check if a date is expired using SERVER time
 */
export function isExpiredServerTime(expiresAt: string): boolean {
  const expiresAtMs = new Date(expiresAt).getTime();
  const serverNowMs = getServerNow();
  return expiresAtMs < serverNowMs;
}

/**
 * Calculate time remaining using SERVER time
 * Returns milliseconds until expiration
 */
export function getTimeRemaining(expiresAt: string): number {
  const expiresAtMs = new Date(expiresAt).getTime();
  const serverNowMs = getServerNow();
  return expiresAtMs - serverNowMs;
}

/**
 * Format time remaining for display
 * Uses SERVER time for consistency across all devices
 */
export function formatTimeRemaining(expiresAt: string): string {
  const diffMs = getTimeRemaining(expiresAt);
  
  if (diffMs <= 0) return "expired";
  
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
  if (diffHours >= 24) {
    const days = Math.floor(diffHours / 24);
    return `in ${days}d ${diffHours % 24}h`;
  } else if (diffHours > 0) {
    return `in ${diffHours}h ${diffMinutes}m`;
  } else {
    return `in ${diffMinutes}m`;
  }
}

// Auto-sync on module load
syncServerTime();

// Re-sync every 60 seconds
if (typeof window !== 'undefined') {
  setInterval(syncServerTime, SYNC_INTERVAL);
}
